/* src/components/EditProfileModal.js */
import React, { useState, useEffect } from 'react';
import { 
  EmailAuthProvider, 
  reauthenticateWithCredential, 
  updatePassword, 
  updateProfile 
} from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { 
  FaUser, 
  FaEnvelope, 
  FaLock, 
  FaTimes, 
  FaCheckCircle, 
  FaShieldAlt,
  FaSpinner
} from 'react-icons/fa';
import './EditProfileModal.css';

const EditProfileModal = ({ isOpen, onClose }) => {
  const { userData, currentUser } = useAuth();
  const { showNotification } = useNotification();
  
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Sincronizar datos iniciales
  useEffect(() => {
    if (isOpen && userData) {
      setFormData({
        displayName: userData.displayName || '',
        email: currentUser?.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setFieldErrors({});
    }
  }, [isOpen, userData, currentUser]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.displayName.trim()) {
      errors.displayName = true;
      showNotification('El nombre no puede estar vacío.', 'error');
    }
    if (!formData.currentPassword) {
      errors.currentPassword = true;
      showNotification('Debes ingresar tu contraseña actual para confirmar los cambios.', 'error');
    }
    if (formData.newPassword) {
      if (formData.newPassword.length < 6) {
        errors.newPassword = true;
        showNotification('La nueva contraseña debe tener al menos 6 caracteres.', 'error');
      }
      if (formData.newPassword !== formData.confirmPassword) {
        errors.confirmPassword = true;
        showNotification('Las nuevas contraseñas no coinciden.', 'error');
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setFieldErrors({});

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No se encontró el usuario actual.');

      // 1. Re-autenticación (Requerido por Firebase para cambios sensibles y solicitado por el usuario)
      const credential = EmailAuthProvider.credential(user.email, formData.currentPassword);
      await reauthenticateWithCredential(user, credential);

      // 2. Actualizar Nombre (si cambió)
      const nameChanged = formData.displayName !== (userData.displayName || userData.nombreCompleto);
      if (nameChanged) {
        // Actualizar perfil de Auth
        await updateProfile(user, { displayName: formData.displayName });
        
        // Actualizar Firestore (solo campo displayName según instrucción)
        const userRef = doc(db, 'usuarios', user.uid);
        await updateDoc(userRef, { 
          displayName: formData.displayName 
        });
        
        console.log('EditProfileModal: Nombre actualizado en Auth y Firestore.');
      }

      // 3. Actualizar Contraseña (si se ingresó una nueva)
      if (formData.newPassword) {
        await updatePassword(user, formData.newPassword);
        console.log('EditProfileModal: Contraseña actualizada.');
      }

      showNotification('Perfil actualizado con éxito.', 'success');
      onClose();
    } catch (err) {
      console.error('Error al actualizar perfil:', err);
      let message = 'Ocurrió un error al actualizar el perfil. Intenta de nuevo.';
      
      if (err.code === 'auth/wrong-password') {
        message = 'La contraseña actual es incorrecta.';
        setFieldErrors({ currentPassword: true });
      } else if (err.code === 'auth/requires-recent-login') {
        message = 'Por seguridad, debes iniciar sesión de nuevo para realizar este cambio.';
      }
      
      showNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const initials = formData.displayName
    ? formData.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  return (
    <div className="ep-modal-backdrop" onClick={onClose}>
      <div className="ep-modal" onClick={e => e.stopPropagation()}>
        <button className="ep-close-btn" onClick={onClose} aria-label="Cerrar">
          <FaTimes />
        </button>

        <header className="ep-header">
          <div className="ep-avatar-container">
            {initials}
          </div>
          <h2>{formData.displayName || 'Mi Perfil'}</h2>
          <p>Actualiza tu información personal</p>
        </header>

        <form onSubmit={handleSave} className="ep-body">
          <div className="ep-form-group">
            <label htmlFor="displayName">Nombre Completo</label>
            <div className="ep-input-wrapper">
              <FaUser />
              <input
                id="displayName"
                name="displayName"
                type="text"
                className={`ep-input ${fieldErrors.displayName ? 'is-invalid' : ''}`}
                placeholder="Tu nombre"
                value={formData.displayName}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </div>

          <div className="ep-form-group">
            <label htmlFor="email">Correo Electrónico</label>
            <div className="ep-input-wrapper">
              <FaEnvelope />
              <input
                id="email"
                name="email"
                type="email"
                className="ep-input"
                value={formData.email}
                disabled
                title="El correo no se puede editar"
              />
            </div>
          </div>

          <div className="ep-divider">
            <span>Seguridad</span>
          </div>

          <div className="ep-form-group">
            <label htmlFor="currentPassword">Contraseña Actual (Requerida)</label>
            <div className="ep-input-wrapper">
              <FaLock />
              <input
                id="currentPassword"
                name="currentPassword"
                type="password"
                className={`ep-input ${fieldErrors.currentPassword ? 'is-invalid' : ''}`}
                placeholder="********"
                value={formData.currentPassword}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </div>

          <div className="ep-form-group">
            <label htmlFor="newPassword">Nueva Contraseña (Opcional)</label>
            <div className="ep-input-wrapper">
              <FaShieldAlt />
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                className={`ep-input ${fieldErrors.newPassword ? 'is-invalid' : ''}`}
                placeholder="Mínimo 6 caracteres"
                value={formData.newPassword}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </div>

          {formData.newPassword && (
            <div className="ep-form-group">
              <label htmlFor="confirmPassword">Confirmar Nueva Contraseña</label>
              <div className="ep-input-wrapper">
                <FaCheckCircle />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  className={`ep-input ${fieldErrors.confirmPassword ? 'is-invalid' : ''}`}
                  placeholder="********"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
            </div>
          )}
        </form>

        <footer className="ep-footer">
          <button 
            type="button" 
            className="ep-btn-cancel" 
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            className="ep-btn-save" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? (
              <>
                <FaSpinner className="fa-spin" /> Actualizando...
              </>
            ) : (
              'Guardar Cambios'
            )}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default EditProfileModal;
